// 後台月排班：可上總覽 / 匯入班表
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ScheduleMonthShell, { monthNow } from './shift/ScheduleMonthShell';
import MonthCalendarPanel from './shift/MonthCalendarPanel';
import ScheduleBoard from './shift/ScheduleBoard';
import ScheduleFormatHelp from './shift/ScheduleFormatHelp';

function adminToken() {
  try { return localStorage.getItem('admin_token') || ''; } catch { return ''; }
}

async function api(url, opts = {}) {
  const method = String(opts.method || 'GET').toUpperCase();
  const headers = { Authorization: `Bearer ${adminToken()}`, ...(opts.headers || {}) };
  if (method !== 'GET' && method !== 'HEAD' && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(url, { ...opts, headers });
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    const data = await res.json();
    if (!res.ok && !data.error) data.error = `http_${res.status}`;
    data._httpStatus = res.status;
    if (!res.ok) {
      console.error('[ScheduleMonthAdmin] API error:', url, data);
    }
    return data;
  }
  let text = '';
  try { text = await res.text(); } catch { /* ignore */ }
  const payload = {
    ok: false,
    error: `http_${res.status}`,
    message: text ? text.slice(0, 300) : `HTTP ${res.status}`,
    _responseBody: text ? text.slice(0, 300) : '',
    _httpStatus: res.status,
  };
  console.error('[ScheduleMonthAdmin] non-JSON response:', url, payload);
  return payload;
}

function formatLoadError(prefix, r) {
  const map = {
    schedule_tables_missing: '月排班資料表尚未建立，請執行 migrate SQL',
    bad_month: '月份格式錯誤',
    bad_request: '請求參數錯誤',
    server_error: '伺服器錯誤',
    no_token: '請重新登入後台',
    forbidden: '無權限',
    invalid_token: '登入已過期',
    http_400: '請求格式錯誤',
    http_401: '未授權',
    http_403: '禁止存取',
    http_404: '找不到 API',
    http_500: '伺服器錯誤',
    http_503: '服務暫不可用',
    incomplete_submissions: '可上資料不完整',
    bad_json: '班表 JSON 格式錯誤',
  };
  const code = r?.error || '';
  const parts = [
    map[code] || code,
    r?.message && r.message !== `HTTP ${r._httpStatus}` ? r.message : null,
    r?._responseBody && code.startsWith('http_') ? r._responseBody.slice(0, 200) : null,
    r?._httpStatus ? `HTTP ${r._httpStatus}` : '',
  ].filter(Boolean);
  if (code.startsWith('http_')) {
    parts.push('（詳見主控台 Network）');
  }
  return parts.length ? `${prefix}：${parts.join(' · ')}` : prefix;
}

const S = {
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 16 },
};

export default function ScheduleMonthAdmin() {
  const [tab, setTab] = useState('month');
  const [month, setMonth] = useState(monthNow(1));
  const [msg, setMsg] = useState('');
  const [overview, setOverview] = useState(null);
  const [schedulePack, setSchedulePack] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importText, setImportText] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [windowForm, setWindowForm] = useState({ openAt: '', closeAt: '' });
  const [boardMode, setBoardMode] = useState('edit');
  const [availEdit, setAvailEdit] = useState(null);

  const loadOverview = useCallback(async () => {
    const r = await api(`/api/admin/shift/schedule-month/overview?month=${month}`);
    if (r?.ok) setOverview(r);
    else setMsg(formatLoadError('可上總覽載入失敗', r));
  }, [month]);

  const loadSchedule = useCallback(async () => {
    const r = await api(`/api/admin/shift/schedule-month/schedule?month=${month}`);
    if (r?.ok) {
      setSchedulePack(r);
      return;
    }
    if (r?.error === 'schedule_missing') return;
    setMsg(formatLoadError('班表載入失敗', r));
  }, [month]);

  useEffect(() => {
    setMsg('');
    if (tab === 'month' || tab === 'schedule') loadOverview();
    if (tab === 'schedule') loadSchedule();
  }, [tab, month, loadOverview, loadSchedule]);

  useEffect(() => {
    const fw = overview?.lock?.fillWindow || overview?.lock;
    const toLocal = (v) => {
      if (!v) return '';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setWindowForm({
      openAt: toLocal(fw?.openAt),
      closeAt: toLocal(fw?.closeAt),
    });
  }, [overview]);

  const toggleLock = async () => {
    const next = !overview?.lock?.isLocked;
    const r = await api('/api/admin/shift/schedule-month/lock', {
      method: 'POST', body: JSON.stringify({ month, isLocked: next }),
    });
    if (r?.ok) { setMsg(next ? '已鎖定填寫' : '已解除鎖定'); loadOverview(); }
    else setMsg(r?.error || '操作失敗');
  };

  const importJson = async () => {
    if (schedulePack?.schedule) {
      const ok = window.confirm('匯入會覆蓋本月現有班表。確定？');
      if (!ok) return;
    }
    setBusy(true);
    try {
      let schedule;
      try { schedule = JSON.parse(importText); } catch { setMsg('JSON 格式錯誤'); return; }
      const r = await api('/api/admin/shift/schedule-month/import-json', {
        method: 'POST', body: JSON.stringify({ month, schedule }),
      });
      if (r?.ok) { setMsg('已匯入'); setImportText(''); setImportOpen(false); loadSchedule(); setBoardMode('edit'); }
      else setMsg(formatLoadError('匯入失敗', r));
    } finally {
      setBusy(false);
    }
  };

  const downloadAvail = () => {
    fetch(`/api/admin/shift/schedule-month/export-availability.json?month=${month}`, {
      headers: { Authorization: `Bearer ${adminToken()}` },
    }).then(async (res) => {
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `availability-${month}.json`;
      a.click();
    });
  };

  const downloadXlsx = () => {
    fetch(`/api/admin/shift/schedule-month/export.xlsx?month=${month}`, {
      headers: { Authorization: `Bearer ${adminToken()}` },
    }).then(async (res) => {
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `schedule-${month}.xlsx`;
      a.click();
    });
  };

  const saveWindow = async () => {
    const r = await api('/api/admin/shift/schedule-month/lock', {
      method: 'POST',
      body: JSON.stringify({
        month,
        openAt: windowForm.openAt ? new Date(windowForm.openAt).toISOString() : null,
        closeAt: windowForm.closeAt ? new Date(windowForm.closeAt).toISOString() : null,
      }),
    });
    if (r?.ok) { setMsg('已儲存填寫區間'); loadOverview(); }
    else setMsg(r?.error || '儲存失敗');
  };

  const patchAvailOverview = async (person, day, entry) => {
    setSaveStatus('儲存中');
    const r = await api('/api/admin/shift/schedule-month/cell', {
      method: 'PATCH',
      body: JSON.stringify({
        month,
        kind: 'availability',
        staffPhone: person.staffPhone,
        person: person.person,
        day,
        entry: { day, base: entry?.base || null, overrides: entry?.overrides || [], note: entry?.note || '' },
      }),
    });
    if (r?.ok) {
      loadOverview();
      setAvailEdit(null);
      setSaveStatus('已儲存');
      setTimeout(() => setSaveStatus(''), 1200);
    } else {
      setMsg(r?.error || '代填失敗');
      setSaveStatus('');
    }
  };

  const patchSheet = async (payload) => {
    setSaveStatus('儲存中');
    const r = await api('/api/admin/shift/schedule-month/cell', {
      method: 'PATCH',
      body: JSON.stringify({ month, ...payload }),
    });
    if (r?.ok) {
      setSchedulePack((p) => ({
        ...(p || {}),
        schedule: r.schedule,
        people: r.people || p?.people,
        notes: r.schedule?.notes,
        shopNotesByDay: r.schedule?.shopNotesByDay,
        volunteersByDay: r.schedule?.volunteersByDay,
        dayNotes: r.schedule?.dayNotes,
      }));
      setSaveStatus('已存');
      setTimeout(() => setSaveStatus(''), 1600);
      if (payload.kind === 'availability' || payload.kind === 'personNote') loadSchedule();
    } else {
      setSaveStatus('');
      setMsg(r?.error || '更新失敗');
    }
  };

  const gapList = useMemo(() => {
    const needs = schedulePack?.schedule?.needsHumanReview || [];
    const violations = (schedulePack?.schedule?.violations || []).map((v) => ({
      day: v.day,
      reason: v.person
        ? `${v.person}：${v.reason || v.description || v.message || '違規'}`
        : (v.reason || v.description || v.message || '違規'),
    }));
    return [...needs, ...violations];
  }, [schedulePack]);

  const jumpToDay = useCallback((day) => {
    const el = document.querySelector(`[data-ss-day="${day}"]`);
    el?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, []);

  const sheetNotes = schedulePack?.schedule?.notes || {};

  const submittedSet = useMemo(() => {
    const s = new Set();
    for (const p of overview?.people || []) if (p.submitted) s.add(p.staffPhone);
    return s;
  }, [overview]);

  return (
    <ScheduleMonthShell month={month} onMonthChange={setMonth} tab={tab} onTabChange={setTab}>
      {msg && (
        <div style={{ ...S.card, marginBottom: 10, background: '#fff7ed', borderColor: '#fed7aa', color: '#9a3412', fontWeight: 600 }}>
          {msg}
        </div>
      )}

      {tab === 'month' && (
        <>
          <ScheduleFormatHelp kind="avail" />
          <MonthCalendarPanel
            month={month}
            overview={overview}
            windowForm={windowForm}
            setWindowForm={setWindowForm}
            onSaveWindow={saveWindow}
            onToggleLock={toggleLock}
            onDownloadAvail={downloadAvail}
            availEdit={availEdit}
            setAvailEdit={setAvailEdit}
            onPatchAvail={patchAvailOverview}
          />
        </>
      )}

      {tab === 'schedule' && (
        <>
          <ScheduleFormatHelp kind="schedule" />
          <ScheduleBoard
            month={month}
            schedulePack={schedulePack}
            overview={overview}
            busy={busy}
            boardMode={boardMode}
            setBoardMode={setBoardMode}
            saveStatus={saveStatus}
            submittedSet={submittedSet}
            gapList={gapList}
            importOpen={importOpen}
            setImportOpen={setImportOpen}
            importText={importText}
            setImportText={setImportText}
            sheetNotes={sheetNotes}
            onImportJson={importJson}
            onDownloadXlsx={downloadXlsx}
            onJumpDay={jumpToDay}
            onPatch={patchSheet}
          />
        </>
      )}
    </ScheduleMonthShell>
  );
}
